import type { StandardSchemaV1 } from "@standard-schema/spec";
import type { BaseLogger, MethodRequestConfig, Nullish } from "./types";
import type { AxiosInstance, AxiosResponse } from "axios";
import axiosInstance, { isAxiosError } from "axios";
import { EMPTY_LOGGER } from "./logger";

declare module "axios" {
  interface AxiosRequestConfig {
    body?: AxiosRequestConfig["data"];
  }
}

export interface RESTFulInterface {
  /**
   * read data
   */
  get<Res extends StandardSchemaV1>(
    url: string,
    response: Res,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>>;
  /**
   * create data
   */
  post<Res extends StandardSchemaV1>(
    url: string,
    response: Res,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>>;
  /**
   * update/modify data
   * @param url
   * @param region
   * @param options
   */
  patch<Res extends StandardSchemaV1>(
    url: string,
    response: Res,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>>;
  /**
   * update/replace data
   * @param url
   * @param region
   * @param options
   */
  put<Res extends StandardSchemaV1>(
    url: string,
    response: Res,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>>;
  /**
   * delete data
   * @param url
   * @param region
   * @param options
   */
  del<Res extends StandardSchemaV1>(
    url: string,
    response: Res,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>>;
  /**
   * delete data
   * @param url
   * @param region
   * @param options
   */
  delete<Res extends StandardSchemaV1>(
    url: string,
    response: Res,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>>;
}

export type RESTResponse<S extends StandardSchemaV1 = StandardSchemaV1<never, never>> =
  AxiosResponse<StandardSchemaV1.InferOutput<S>>;

export class AxiosRESTDataSource implements RESTFulInterface {
  public baseURL?: string;

  constructor(
    public readonly axios: AxiosInstance = axiosInstance,
    public logger: BaseLogger = EMPTY_LOGGER,
  ) {}

  async fetch<Res extends StandardSchemaV1>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    url: string,
    response: Nullish<Res>,
    options?: MethodRequestConfig,
  ) {
    const data = options?.body ?? options?.data;
    delete options?.body;
    const headers = options?.headers;
    const realUrl = this.baseURL ? `${this.baseURL}${url}` : url;
    const fullUrl = this.axios.defaults.baseURL
      ? `${this.axios.defaults.baseURL}${realUrl}`
      : realUrl;
    this.logger.info({ method, url: fullUrl });
    this.logger.debug({ method, url: fullUrl, headers: headers });
    try {
      const res = await this.axios<StandardSchemaV1.InferOutput<Res>>({
        method,
        url: realUrl,
        data,
        ...options,
        schema: {
          response,
        },
        headers,
      });

      if (!res) {
        // should not be reached, if there is no response, it should throw an error
        // if it reaches here, it means there is a bug, it might be caused by interceptors or something else

        debugger;
      }
      this.logger.info({ method, url: fullUrl, status: res.status });
      return res;
    } catch (e) {
      const status = isAxiosError(e) ? e.response?.status : undefined;
      this.logger.warn(
        { method, url: fullUrl, status },
        e instanceof Error
          ? e.stack
            ? `${e.stack}`
            : `${e.name} ${e.message}`
          : (e as any).toString(),
      );
      throw e;
    }
  }

  get<Res extends StandardSchemaV1>(
    url: string,
    response: Nullish<Res>,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>> {
    return this.fetch("GET", url, response, options);
  }

  post<Res extends StandardSchemaV1>(
    url: string,
    response: Nullish<Res>,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>> {
    return this.fetch("POST", url, response, options);
  }

  delete<Res extends StandardSchemaV1>(
    url: string,
    response: Res | undefined | null,
    options?: MethodRequestConfig,
  ) {
    return this.fetch("DELETE", url, response, options);
  }

  del<Res extends StandardSchemaV1>(
    url: string,
    response: Nullish<Res>,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>> {
    return this.fetch("DELETE", url, response, options);
  }

  patch<Res extends StandardSchemaV1>(
    url: string,
    response: Nullish<Res>,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>> {
    return this.fetch("PATCH", url, response, options);
  }

  put<Res extends StandardSchemaV1>(
    url: string,
    response: Nullish<Res>,
    options?: MethodRequestConfig,
  ): Promise<RESTResponse<Res>> {
    return this.fetch("PUT", url, response, options);
  }
}
